import { Theme } from '@mui/material/styles/createTheme';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { atom, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { endingDate } from '../../../../recoil/atoms/endingDate';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../../services/rest/makeAPIRequest';
import { IVwQueueMembers } from '../../../../services/rest/makeApiRequest.t';
import { momentTz } from '../../../components/subheader/components/DatePicker';
import Banner from '../Banner';
import { hhmmss } from '../Leaderboard';
import { ISlideComponentProps, QueueSlide, Slide, SlideEvent } from '../Slide';

const useStyles = makeStyles((theme: Theme) => ({
  queueSlideContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,

    transition: 'opacity 1000ms ease-out',
    opacity: 0,
  },
  queueSlideContainerVisible: {
    opacity: 1,
  },
  sideBySideContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  leftContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginRight: theme.spacing(2),
    minWidth: 500,
  },
  rightContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    flex: 1,
    marginRight: theme.spacing(4),
  },
  bottomContainer: {
    display: 'flex',
    marginTop: 0,
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',
    justifyContent: 'center',
  },
  bannerContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    height: 130,

    [theme.breakpoints.down('lg')]: {
      height: 160,
    },
  },
  availabilityLevelContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    marginLeft: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',

    '& h4': {
      fontWeight: 'bold',
    },
  },
  availabilityLevelDescription: {
    fontSize: 18,
    color: '#999',
  },
  availabilityLevelProgress: {
    display: 'flex',
    width: 150,
    height: 150,
    border: '20px solid transparent',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    fontWeight: 'bold',
    color: '#459AC6',
    background: `linear-gradient(#fff, #fff) content-box no-repeat,
                 conic-gradient(#F8F5F6 var(--percentage, 0), #459AC6 var(--percentage, 0)) border-box`,
    '--percentage': `0deg`,
  },
  memberAvailabilityContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    height: 130,

    [theme.breakpoints.down('lg')]: {
      height: 160,
    },

    '& h4': {
      fontWeight: 'bold',
    },
    '& div': {
      marginTop: theme.spacing(1),
      borderRight: '1px solid #ccc',
    },
    '& div:last-child': {
      borderRight: 'none',
    },
  },
  memberAvailabilityStats: {
    display: 'flex',
    flexDirection: 'row',

    '& div': {
      flex: 1,
    },
  },
  memberAvailabilityStat: {
    fontSize: 28,
    fontWeight: 'bold',
    lineHeight: 'normal',
    margin: 0,
  },
  memberAvailabilityStatLabel: {
    margin: 0,
    color: '#888',
  },
  inboundCallTimeContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',

    '& h4': {
      fontWeight: 'bold',
      marginBottom: theme.spacing(4),
    },
  },
  inboundCallTimes: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
  },
  avgWaitingTime: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 'normal',
    margin: 0,
    marginTop: theme.spacing(1),
  },
  avgWaitingTimeLabel: {
    lineHeight: 'normal',
    margin: 0,
    marginBottom: theme.spacing(1),
    color: '#999',
  },
  longestWaitingTime: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 'normal',
    margin: 0,
    marginTop: theme.spacing(1),
  },
  longestWaitingTimeLabel: {
    lineHeight: 'normal',
    margin: 0,
    marginBottom: theme.spacing(1),
    color: '#999',
  },
  waitingTimeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  waitingTimeStat: {
    flex: 1,

    '&:first-child': {
      borderRight: '1px solid #ccc',
    },
  },
  inboundCallsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingBottom: theme.spacing(4),

    '& h4': {
      fontWeight: 'bold',
      marginBottom: theme.spacing(4),
    },
  },
  inboundCallsStats: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  totalInboundCallsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    marginRight: theme.spacing(4),
    paddingRight: theme.spacing(4),
    borderRight: '1px solid #ccc',

    [theme.breakpoints.down('lg')]: {
      marginRight: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },

    '& p': {
      fontSize: 32,
      fontWeight: 'bold',
      margin: 0,
      marginTop: theme.spacing(1),
      lineHeight: 'normal',
    },
    '& h5': {
      marginBottom: theme.spacing(1),
      color: '#999',
    },
  },
  inboundCallStatContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    margin: theme.spacing(2),
    textAlign: 'left',

    [theme.breakpoints.down('lg')]: {
      margin: theme.spacing(1),
      marginRight: 0,
    },

    '& h5': {
      margin: 0,
      fontSize: 16,
      fontWeight: 'bold',
      color: '#999',
    },
  },
  inboundCallStat: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  inboundCallStatValues: {},
  inboundCallStatPrimary: {
    fontSize: 24,
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
    lineHeight: 'normal',

    [theme.breakpoints.down('lg')]: {
      marginRight: 0,
    },
  },
  inboundCallStatSecondary: {
    fontSize: 16,
    color: '#999',
    lineHeight: 'normal',

    [theme.breakpoints.down('lg')]: {
      display: 'block',
    },
  },
  inboundCallStatProgress: {
    display: 'flex',
    width: 50,
    height: 50,
    marginRight: theme.spacing(2),
    border: '6px solid transparent',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(#fff, #fff) content-box no-repeat,
                 conic-gradient(#F8F5F6 var(--percentage, 0), #459AC6 var(--percentage, 0)) border-box`,
    '--percentage': `0deg`,

    [theme.breakpoints.down('lg')]: {
      marginRight: theme.spacing(1),
    },
  },
}));

interface IQueuesReportResponse {
  id: number;
  queueid: string;
  queuename: string;
  abandoned: number;
  answered: number;
  missed: number;
  // timetoRetrieved: number;
  // 'avg total hold': number;
  // 'avg total ring': number;
  // 'avg time to abandon': string;
  // holdtime: string;
  abandontime: number;
  // ringtime: string;
  // holdtimesort: number;
  // abandontimesort: number;
  // ringtimesort: number;
  total: number;
}

const getDefaultQueuesReportResponse = (
  slide: QueueSlide
): IQueuesReportResponse => ({
  id: Number(slide.queueId),
  queueid: slide.queueId.toString(),
  queuename: slide.slideName ?? '',
  abandoned: 0,
  answered: 0,
  missed: 0,
  abandontime: 0,
  total: 0,
});

interface IQueuesLiveResponse {
  id: number;
  queueid: string;
  queuename: string;
  holding: number;
  // maxholdtime: number;
  avgholdtime: number;
  // minholdtime: number;
  // requestCallback: number;
  // maxRequestCallbackTime: string;
  // avgRequestCallbackTime: string;
  // minRequestCallbackTime: string;
  // agentsSubscribed: number;
  DurationAvgHold: number;
  DurationMaxHold: number;
  // DurationMinHold: number;
  // DurationAnsweredCount: number;
  // DurationAvgAbandon: string;
  // DurationAbandonedCount: number;
  // DurationMaxAbandon: number;
  // DurationMinAbandon: number;
}

const getDefaultQueuesLiveResponse = (
  slide: QueueSlide
): IQueuesLiveResponse => ({
  id: Number(slide.queueId),
  queueid: slide.queueId.toString(),
  queuename: slide.queueid?.queuename ?? slide.slideName ?? '',
  holding: 0,
  avgholdtime: 0,
  DurationAvgHold: 0,
  DurationMaxHold: 0,
});

interface IQueue {
  queueid: string;
  name: string;
  holding: number;
  avgholdtime: string;
  DurationAvgHold: string;
  DurationMaxHold: string;
  agentsSubscribed: number;
  agentsActiveCall: number;
  agentsDND: number;
  agentsCanReceiveCalls: number;
  agentsAvailable: number;
  agentsActive: number;
  abandoned: number;
  answered: number;
  missed: number;
  abandontime: string;
  total: number;
  answeredPercent: number;
  missedPercent: number;
  abandonedPercent: number;
}

const startingDate = atom({
  key: 'startingDateThirtySecondsAgo',
  default: momentTz(moment().format('YYYY-MM-DD')).subtract(30, 'seconds'),
});

let first = true;

export default function Queue({ slideObservable }: ISlideComponentProps) {
  const classes = useStyles();

  const [isReady, setIsReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoadingQueueReport, setIsLoadingQueueReport] = useState(false);
  const [isLoadingQueueLive, setIsLoadingQueueLive] = useState(false);
  const [isLoadingQueueMembers, setIsLoadingQueueMembers] = useState(false);

  const slide = slideObservable.slide as QueueSlide;

  if (typeof slide?.queueid?.queueid === 'number') {
    slide.queueId = slide.queueid.queueid.toString();
  }

  // BEGIN Taken from QueuesReport and modified
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    })
  );

  const startDate = useRecoilValueLoadable(startingDate);
  const endDate = useRecoilValueLoadable(endingDate);

  const refetchToggleQueuesReport = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_QUEUES_REPORTING)
  );

  const queuesReportLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_QUEUES_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: startDate.contents,
        enddate: endDate.contents,
        timezone:
          businessData?.contents?.response?.data?.result?.[0]?.timezone_php,
      }),
    })
  );

  const [queueReport]: IQueuesReportResponse[] = useMemo(() => {
    const queuesReport =
      queuesReportLoadable.contents?.data?.result
        ?.filter(
          (q) =>
            slide.queueId && q.queueid.toString() === slide.queueId.toString()
        )
        ?.map((o) => ({
          ...o,
          id: o.queueid,
          queueid: o.queueid.toString(),
          queuename: o.queuename ?? '',
          abandontime: Math.ceil(o['avg time to abandon'] ?? 0),
          abandoned: o.abandoned ?? 0,
          missed: o.missed ?? 0,
          answered: o.answered ?? 0,
          total: (o.answered ?? 0) + (o.missed ?? 0) + (o.abandoned ?? 0),
        })) ?? [];

    return queuesReport.length
      ? queuesReport
      : [getDefaultQueuesReportResponse(slide)];
  }, [queuesReportLoadable]);
  // END Taken from QueuesReport and modified

  // BEGIN Taken from QueuesLive and modified
  const refetchToggleQueuesLive = useSetRecoilState(
    refetchToggle(endpointEnum.GET_QUEUES_LIVE_REPORTING)
  );

  const queuesLiveLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_QUEUES_LIVE_REPORTING,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_QUEUES_LIVE_REPORTING,
      }),
    })
  );

  const [queueLive]: IQueuesLiveResponse[] = useMemo(() => {
    const queuesLive =
      queuesLiveLoadable.contents?.response?.data?.result
        ?.filter(
          (q) =>
            slide.queueId && q.queueid.toString() === slide.queueId.toString()
        )
        ?.map((o) => ({
          ...o,
          id: o.queueid,
          queueid: o.queueid.toString(),
          queuename: o?.queuename ?? '',
          holding: o.holding ?? 0,
          avgholdtime: o.avgholdtime ?? 0,
          DurationAvgHold: o.DurationAvgHold ?? 0,
          DurationMaxHold: o.DurationMaxHold ?? 0,
        })) ?? [];

    return queuesLive.length
      ? queuesLive
      : [getDefaultQueuesLiveResponse(slide)];
  }, [queuesLiveLoadable]);
  // END Taken from QueuesLive and modified

  // BEGIN Taken from QueueMembersDialog and modified
  const [
    queueMembersCached,
    queueMembersLoadable,
    refetchToggleQueueMembers,
  ] = makeApiRequestCached<IVwQueueMembers[]>(
    {
      endpoint: endpointEnum.GET_QUEUE_MEMBERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_QUEUE_MEMBERS,
        queueid: slide.queueId,
      }),
      refetchParam: `${endpointEnum.GET_QUEUE_MEMBERS}-${slide.queueId}`,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data
        ?.filter(
          (q) =>
            slide.queueId && q.queueid.toString() === slide.queueId.toString()
        )
        ?.map((o) => ({
          ...o,
          id: o.queueid,
          queueid: o.queueid.toString(),
          queuename: o?.queuename ?? '',
          holding: o.holding ?? 0,
          avgholdtime: o.avgholdtime ?? 0,
          DurationAvgHold: o.DurationAvgHold ?? 0,
          DurationMaxHold: o.DurationMaxHold ?? 0,
        })) ?? cached.current,
    []
  );
  // END Taken from QueueMembersDialog and modified

  useEffect(() => {
    const observer = (_slide: Slide, event: SlideEvent, _index: number) => {
      setIsReady(true);

      switch (event) {
        case SlideEvent.animateInStarted:
          if (first) {
            first = false;
          } else {
            refetchToggleQueuesReport({});
            refetchToggleQueuesLive({});
            refetchToggleQueueMembers({});
          }
          setIsVisible(true);
          break;
        case SlideEvent.animateOutStarted:
          setIsVisible(false);
          break;
      }
    };

    slideObservable.observe(observer);

    return () => {
      slideObservable.unobserve(observer);
    };
  }, [slideObservable]);

  useEffect(() => {
    switch (queuesReportLoadable.state) {
      case 'loading':
        setIsLoadingQueueReport(true);
        break;
      default:
        setIsLoadingQueueReport(false);
    }
  }, [queuesReportLoadable.state]);

  useEffect(() => {
    switch (queuesLiveLoadable.state) {
      case 'loading':
        setIsLoadingQueueLive(true);
        break;
      default:
        setIsLoadingQueueLive(false);
    }
  }, [queuesLiveLoadable.state]);

  useEffect(() => {
    switch (queueMembersLoadable.state) {
      case 'loading':
        setIsLoadingQueueMembers(true);
        break;
      default:
        setIsLoadingQueueMembers(false);
    }
  }, [queueMembersLoadable.state]);

  const [queue, setQueue] = useState<IQueue>();

  useEffect(() => {
    const queueMembers = queueMembersCached.current;

    if (
      !isReady ||
      isLoadingQueueReport ||
      isLoadingQueueLive ||
      isLoadingQueueMembers ||
      !queueReport ||
      !queueLive ||
      !queueMembers
    ) {
      return;
    }

    setQueue({
      queueid: slide.queueId.toString(),
      name: queueLive.queuename ?? queueReport.queuename ?? 'No name',
      holding: queueLive.holding,
      avgholdtime: hhmmss(queueLive.avgholdtime),
      DurationAvgHold: hhmmss(queueLive.DurationAvgHold),
      DurationMaxHold: hhmmss(queueLive.DurationMaxHold),
      agentsSubscribed: queueMembers.filter((m) => !!m.subscribed).length,
      agentsActiveCall: queueMembers.filter((m) => !!m.onCall).length,
      agentsDND: queueMembers.filter((m) => !!m.DND).length,
      agentsCanReceiveCalls: queueMembers.filter((m) => !!m.canReceiveCalls)
        .length,
      agentsAvailable: queueMembers.filter((m) => !!m.agentAvailable).length,
      agentsActive: queueMembers.filter((m) => !!m.userActive).length,
      abandoned: queueReport.abandoned,
      answered: queueReport.answered,
      missed: queueReport.missed,
      abandontime: hhmmss(queueReport.abandontime),
      total: queueReport.total,
      answeredPercent:
        queueReport.total > 0
          ? Math.ceil((queueReport.answered / queueReport.total) * 100)
          : 0,
      missedPercent:
        queueReport.total > 0
          ? Math.ceil((queueReport.missed / queueReport.total) * 100)
          : 0,
      abandonedPercent:
        queueReport.total > 0
          ? Math.ceil((queueReport.abandoned / queueReport.total) * 100)
          : 0,
    });
  }, [
    isReady,
    isLoadingQueueReport,
    isLoadingQueueLive,
    queueReport,
    queueLive,
    queueMembersCached.current,
  ]);

  const toPercentAsInt = (value) => Math.floor(value * 100);

  const availabilityPercent = toPercentAsInt(
    queue && queue.agentsSubscribed > 0
      ? queue.agentsSubscribed / queue.agentsActive
      : 0
  );

  const toPercentage = (percent: number) => `${(100 - percent) * 3.6}deg`;

  return !queue ? null : (
    <div
      className={`${classes.queueSlideContainer} ${
        isVisible ? classes.queueSlideContainerVisible : ''
      }`}
    >
      <div className={classes.sideBySideContainer}>
        <div className={classes.leftContainer}>
          <div className={classes.bannerContainer}>
            <Banner
              isReady={isReady}
              slideObservable={slideObservable}
              smallBannerText={`ID: ${slide.queueId}`}
              largeBannerText={queue.name}
            />
          </div>
          <div className={classes.availabilityLevelContainer}>
            <h4>Availability Level</h4>
            <p className={classes.availabilityLevelDescription}>
              Subscribed agents within queue
            </p>
            <div
              className={classes.availabilityLevelProgress}
              style={
                { '--percentage': toPercentage(availabilityPercent) } as any
              }
            >
              <span>{availabilityPercent}%</span>
            </div>
          </div>
        </div>
        <div className={classes.rightContainer}>
          <div className={classes.memberAvailabilityContainer}>
            <h4>Member Availability</h4>
            <div className={classes.memberAvailabilityStats}>
              <div>
                <p className={classes.memberAvailabilityStat}>
                  {queue.agentsSubscribed}
                </p>
                <p className={classes.memberAvailabilityStatLabel}>
                  Subscribed
                </p>
              </div>
              <div>
                <p className={classes.memberAvailabilityStat}>
                  {queue.agentsActiveCall}
                </p>
                <p className={classes.memberAvailabilityStatLabel}>
                  Active Call
                </p>
              </div>
              <div>
                <p className={classes.memberAvailabilityStat}>
                  {queue.agentsDND}
                </p>
                <p className={classes.memberAvailabilityStatLabel}>
                  Do Not Disturb
                </p>
              </div>
              <div>
                <p className={classes.memberAvailabilityStat}>
                  {queue.agentsCanReceiveCalls}
                </p>
                <p className={classes.memberAvailabilityStatLabel}>
                  Can Receive Calls
                </p>
              </div>
              <div>
                <p className={classes.memberAvailabilityStat}>
                  {queue.agentsAvailable}
                </p>
                <p className={classes.memberAvailabilityStatLabel}>Available</p>
              </div>
            </div>
          </div>
          <div className={classes.inboundCallTimeContainer}>
            <h4>Inbound Call Time</h4>
            <div className={classes.inboundCallTimes}>
              <div className={classes.waitingTimeContainer}>
                <div className={classes.waitingTimeStat}>
                  <p className={classes.avgWaitingTime}>
                    {queue.DurationAvgHold}
                  </p>
                  <p className={classes.avgWaitingTimeLabel}>
                    Avg. Waiting Time
                  </p>
                </div>
                <div className={classes.waitingTimeStat}>
                  <p className={classes.longestWaitingTime}>
                    {queue.DurationMaxHold}
                  </p>
                  <p className={classes.longestWaitingTimeLabel}>
                    Longest Waiting Time
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={classes.bottomContainer}>
        <div className={classes.inboundCallsContainer}>
          <h4>Inbound Calls</h4>
          <div className={classes.inboundCallsStats}>
            <div className={classes.totalInboundCallsContainer}>
              <p>{queue.total}</p>
              <h5>Total Inbound Calls</h5>
            </div>
            <div className={classes.inboundCallStatContainer}>
              <div
                className={classes.inboundCallStatProgress}
                style={
                  {
                    '--percentage': toPercentage(queue.answeredPercent),
                  } as any
                }
              />
              <div className={classes.inboundCallStat}>
                <h5>Answered</h5>
                <div className={classes.inboundCallStatValues}>
                  <span className={classes.inboundCallStatPrimary}>
                    {queue.answered}
                  </span>
                  <span className={classes.inboundCallStatSecondary}>
                    {queue.answeredPercent}%
                  </span>
                </div>
              </div>
            </div>
            <div className={classes.inboundCallStatContainer}>
              <div
                className={classes.inboundCallStatProgress}
                style={
                  {
                    '--percentage': toPercentage(queue.abandonedPercent),
                  } as any
                }
              />
              <div className={classes.inboundCallStat}>
                <h5>Abandoned</h5>
                <div className={classes.inboundCallStatValues}>
                  <span className={classes.inboundCallStatPrimary}>
                    {queue.abandoned}
                  </span>
                  <span className={classes.inboundCallStatSecondary}>
                    {queue.abandontime}
                  </span>
                </div>
              </div>
            </div>
            <div className={classes.inboundCallStatContainer}>
              <div className={classes.inboundCallStat}>
                <h5>On Hold</h5>
                <div className={classes.inboundCallStatValues}>
                  <span className={classes.inboundCallStatPrimary}>
                    {queue.holding}
                  </span>
                  <span className={classes.inboundCallStatSecondary}>
                    {queue.avgholdtime}
                  </span>
                </div>
              </div>
            </div>
            <div className={classes.inboundCallStatContainer}>
              <div
                className={classes.inboundCallStatProgress}
                style={
                  {
                    '--percentage': toPercentage(queue.missedPercent),
                  } as any
                }
              />
              <div className={classes.inboundCallStat}>
                <h5>Missed</h5>
                <div className={classes.inboundCallStatValues}>
                  <span className={classes.inboundCallStatPrimary}>
                    {queue.missed}
                  </span>
                  <span className={classes.inboundCallStatSecondary}>
                    {queue.missedPercent}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
